/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";
import {
	type CSSProperties,
	type FormHTMLAttributes,
	forwardRef,
	useRef,
} from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { LayoutRenderer } from "@/lib/layoutRenderer/components/layout-renderer";
import { buildLayoutFieldTypeIndex } from "@/lib/layoutRenderer/utils/layout-field-type-index.util";
import {
	DynamicFormProvider,
	useDynamicFormContext,
} from "../contexts/DynamicFormContext";
import type {
	DynamicFormSchema,
	FormField,
	FormValue,
} from "../interfaces/dynamicForm.interface";
import type {
	ElementRendererRegistry,
	FieldRendererRegistry,
} from "../interfaces/renderer.interface";
import {
	RendererProvider,
	useOptionalRendererContext,
} from "../renderers/cleanRendererSystem";
import { generateZodSchema } from "../utils/generateZodSchema.util";
import { persistedDefinitionToFormField } from "../utils/persisted-field-definition.util";
import {
	type DynamicFormEntityKind,
	type DynamicFormRequirementMode,
	enforcesRequiredFields,
} from "../utils/requirement-mode.util";
import { DynamicFormRequirementNotice } from "./dynamic-form-requirement-notice";

function formFieldsFromDefinitions(schema: DynamicFormSchema): FormField[] {
	const layoutFieldTypeByDefinitionKey = buildLayoutFieldTypeIndex(
		schema.layoutJson,
	);
	return schema.fieldDefinitions
		.map((d) =>
			persistedDefinitionToFormField(d, {
				layoutFieldTypeByDefinitionKey,
			}),
		)
		.filter((f): f is FormField => f != null);
}

// Helper function to convert date and datetime values to Unix timestamps
function convertDatesToTimestamps(
	data: Record<string, unknown>,
	schema: DynamicFormSchema,
): Record<string, unknown> {
	const converted = { ...data };
	function processFields(
		fields: FormField[],
		dataObj: Record<string, unknown>,
	) {
		for (const field of fields) {
			const fieldName = field.name;
			const fieldValue = dataObj[fieldName];

			if (
				fieldValue &&
				typeof fieldValue === "string" &&
				fieldValue.trim() !== ""
			) {
				if (
					field.type === "date" ||
					field.type === "time" ||
					field.type === "dateTime"
				) {
					try {
						const date = new Date(fieldValue);
						if (!isNaN(date.getTime())) {
							// Convert to Unix timestamp in milliseconds
							converted[fieldName] = date.getTime();
						}
					} catch {
						console.warn(
							`Failed to convert date value for field ${fieldName}:`,
							fieldValue,
						);
					}
				}
			}
			if (field.type === "group" && field.fields) {
				processFields(field.fields, dataObj);
			}
			if (
				field.type === "fieldArray" &&
				field.fields &&
				Array.isArray(fieldValue)
			) {
				fieldValue.forEach((item: Record<string, unknown>) => {
					processFields(field.fields, item);
				});
			}
		}
	}
	processFields(formFieldsFromDefinitions(schema), converted);
	return converted;
}

// Helper function to convert autocomplete field values to extract option.value before validation
function convertAutocompleteValues(
	data: Record<string, unknown>,
	schema: DynamicFormSchema,
): Record<string, unknown> {
	const converted = { ...data };
	function processFields(
		fields: FormField[],
		dataObj: Record<string, unknown>,
	) {
		for (const field of fields) {
			const fieldName = field.name;
			const fieldValue = dataObj[fieldName];

			if (field.type === "autocomplete" && fieldValue) {
				if (field.multiple && Array.isArray(fieldValue)) {
					// Handle multiple selection - extract value from each option object
					converted[fieldName] = fieldValue.map((option: unknown) => {
						if (
							option &&
							typeof option === "object" &&
							option !== null &&
							"value" in option
						) {
							return (option as { value: string }).value;
						}
						return option; // Fallback if not an option object
					});
				} else if (
					typeof fieldValue === "object" &&
					fieldValue !== null &&
					"value" in fieldValue
				) {
					// Handle single selection - extract value from option object
					converted[fieldName] = (fieldValue as { value: string }).value;
				}
			}

			if (field.type === "group" && field.fields) {
				processFields(field.fields, dataObj);
			}
			if (
				field.type === "fieldArray" &&
				field.fields &&
				Array.isArray(fieldValue)
			) {
				fieldValue.forEach((item: Record<string, unknown>) => {
					processFields(field.fields, item);
				});
			}
		}
	}
	processFields(formFieldsFromDefinitions(schema), converted);
	return converted;
}

interface DynamicFormContentProps
	extends Omit<FormHTMLAttributes<HTMLFormElement>, "children"> {
	schema: DynamicFormSchema;
	CMS?: boolean;
	/** `preview` hides the default submit control and dev logging (e.g. form builder modal). */
	variant?: "default" | "preview";
	requirementMode?: DynamicFormRequirementMode;
	entityKind?: DynamicFormEntityKind;
	children?: React.ReactNode;
}

// Internal component that uses the context
const DynamicFormContent = forwardRef<HTMLFormElement, DynamicFormContentProps>(
	(
		{
			schema,
			children,
			CMS,
			variant = "default",
			requirementMode = "require",
			entityKind,
			...formProps
		},
		ref,
	) => {
		const { form } = useDynamicFormContext() ?? { form: null };
		const isPreview = variant === "preview";

		if (!isPreview) {
			console.log("🔧 DynamicFormContent rendering, CMS mode:", CMS);
			console.log("🔧 Form instance:", form);
		}

		const handleFormSubmit = (e: React.FormEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (isPreview || !form) {
				return;
			}
			form.handleSubmit();
		};

		if (!isPreview) {
			console.log("🔧 Form onSubmit handler attached");
		}

		return (
			<form
				{...formProps}
				onSubmit={handleFormSubmit}
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 8,
					...(formProps.style as CSSProperties | undefined),
				}}
				id={schema.id}
				ref={(node) => {
					if (!isPreview) {
						console.log("📋 Form element ref callback, form element:", node);
						if (node) {
							console.log("📋 Form children count:", node.children.length);
							console.log(
								"📋 Form has submit buttons:",
								node.querySelectorAll('[type="submit"]').length,
							);
						}
					}
					if (typeof ref === "function") {
						ref(node);
					} else if (ref) {
						ref.current = node;
					}
				}}
			>
				{CMS ? (
					children
				) : (
					<>
						{schema.title && <h2>{schema.title}</h2>}
						{schema.description && <p>{schema.description}</p>}
						{schema.layoutJson && (
							<>
								<DynamicFormRequirementNotice
									mode={requirementMode}
									entityKind={entityKind}
									fieldDefinitions={schema.fieldDefinitions}
								/>
								<LayoutRenderer
									schema={schema}
									fieldDefinitions={schema.fieldDefinitions}
									node={schema.layoutJson}
								/>
							</>
						)}
						{/* {schema.fields.map((field: FormField) => (
							<FieldRenderer key={field.name} field={field} />
						))} */}
						{isPreview ? null : (
							<button id="default-submit-button" type="submit">
								Submit
							</button>
						)}
					</>
				)}
			</form>
		);
	},
);

interface DynamicFormProps {
	isLoading?: boolean;
	schema: DynamicFormSchema;
	editorMode?: boolean;
	/** When `preview`, omits default submit and reduces dev-only logging (e.g. ticket type form preview modal). */
	variant?: "default" | "preview";
	requirementMode?: DynamicFormRequirementMode;
	entityKind?: DynamicFormEntityKind;
	children?: React.ReactNode;
	contentProps?: any;
	CMS?: boolean;
	fieldRenderers?: FieldRendererRegistry;
	Renderers?: FieldRendererRegistry;
	initialValues?: Record<string, unknown>;
	onValuesChange?: (
		values: Record<string, unknown>,
		meta: {
			changedField?: string;
			isValid?: boolean;
			phase?: "change" | "blur" | "cancel";
			fieldType?: string;
		},
	) => void;
}

// Helper function to filter out hidden fields from schema
function filterHiddenFieldsFromSchema(
	schema: DynamicFormSchema,
	fieldStates: Record<string, { shouldShow?: boolean }>,
): DynamicFormSchema {
	return {
		...schema,
		fieldDefinitions: schema.fieldDefinitions.filter((def) => {
			const fieldState = fieldStates[def.id];
			const isHidden = fieldState?.shouldShow === false || def.hidden === true;
			return !isHidden;
		}),
	};
}

// Internal component that has access to context and can filter schema
const DynamicFormWithContext = forwardRef<HTMLFormElement, DynamicFormProps>(
	(
		{
			schema,
			editorMode,
			variant = "default",
			requirementMode = "require",
			entityKind,
			children,
			contentProps,
			CMS,
			initialValues,
			onValuesChange,
		},
		ref,
	) => {
		const { t } = useAppTranslation();
		// const { fieldStates } = useDynamicFormContext() || { fieldStates: {} };
		const fieldStates = useRef({});

		const handleFieldStateChange = (
			newFieldStates: Record<string, { shouldShow?: boolean }>,
		) => {
			fieldStates.current = newFieldStates;
		};

		const handleSubmit = (value: FormValue) => {
			alert("🎯 FINAL HANDLER CALLED! Starting Zod validation...");
			console.log("=== FINAL FORM SUBMISSION ===", fieldStates.current);
			console.log("Raw form values:", value);

			// Filter out hidden fields from schema before generating Zod schema
			const filteredSchema = filterHiddenFieldsFromSchema(
				schema,
				fieldStates.current,
			);
			const zodSchema = generateZodSchema(filteredSchema, t, {
				enforceRequired: enforcesRequiredFields(requirementMode),
			});

			// Convert autocomplete field values to extract option.value before validation
			const convertedValues = convertAutocompleteValues(
				value as Record<string, unknown>,
				schema,
			);
			console.log("Converted autocomplete values:", convertedValues);

			const validation = zodSchema.safeParse(convertedValues);
			if (validation.success) {
				console.log("✅ Zod schema validation passed!");
				console.log("Validated data:", validation.data);

				// Convert date and datetime values to Unix timestamps
				const convertedData = convertDatesToTimestamps(
					convertedValues as Record<string, unknown>,
					schema,
				);

				console.log("=== DATA READY FOR API ===");
				console.log(
					"Request Body (with timestamps):",
					JSON.stringify(convertedData, null, 2),
				);
				console.log("=========================");

				alert(
					"✅ ALL VALIDATION PASSED!\n\nForm submitted successfully!\n\nCheck console for the data that would be sent to the API.",
				);
			} else {
				console.error("❌ Validation errors:", validation.error.issues);
				alert(
					"❌ ZOD SCHEMA VALIDATION FAILED!\n\nSome fields have validation errors.\n\nCheck the console for detailed error information.",
				);
			}

			console.log("=== END FORM SUBMISSION ===");
		};

		return (
			<DynamicFormProvider
				schema={schema}
				editorMode={editorMode}
				previewMode={variant === "preview"}
				requirementMode={requirementMode}
				onSubmit={handleSubmit}
				onFieldStateChange={handleFieldStateChange}
				initialValues={initialValues}
				onValuesChange={onValuesChange}
			>
				<DynamicFormContent
					ref={ref}
					{...contentProps}
					schema={schema}
					CMS={CMS}
					variant={variant}
					requirementMode={requirementMode}
					entityKind={entityKind}
				>
					{children}
				</DynamicFormContent>
			</DynamicFormProvider>
		);
	},
);

DynamicFormWithContext.displayName = "DynamicFormWithContext";

type DynamicFormComponent = React.ForwardRefExoticComponent<
	DynamicFormProps & React.RefAttributes<HTMLFormElement>
> & {
	Renderers: FieldRendererRegistry;
};

export const DynamicForm = forwardRef<HTMLFormElement, DynamicFormProps>(
	(
		{
			schema,
			editorMode,
			variant = "default",
			requirementMode = "require",
			entityKind,
			children,
			contentProps,
			CMS,
			fieldRenderers,
			initialValues,
			onValuesChange,
		},
		ref,
	) => {
		const parentContext = useOptionalRendererContext();
		const parentFieldRenderers: FieldRendererRegistry =
			parentContext?.fieldRenderers ?? {};
		const parentElementRenderers: ElementRendererRegistry =
			parentContext?.elementRenderers ?? {};

		// Merge parent field renderers with provided field renderers
		const mergedFieldRenderers = {
			...parentFieldRenderers,
			...fieldRenderers,
		};

		return (
			<RendererProvider
				fieldRenderers={mergedFieldRenderers}
				elementRenderers={parentElementRenderers}
			>
				<DynamicFormWithContext
					ref={ref}
					schema={schema}
					editorMode={editorMode}
					variant={variant}
					requirementMode={requirementMode}
					entityKind={entityKind}
					contentProps={contentProps}
					CMS={CMS}
					initialValues={initialValues}
					onValuesChange={onValuesChange}
				>
					{children}
				</DynamicFormWithContext>
			</RendererProvider>
		);
	},
) as DynamicFormComponent;

// Static property to allow setting custom renderers
DynamicForm.Renderers = {} as FieldRendererRegistry;

// Helper function to set custom renderers
export const setDynamicFormRenderers = (renderers: FieldRendererRegistry) => {
	DynamicForm.Renderers = renderers;
};

// Helper function to get current renderers
export const getDynamicFormRenderers = (): FieldRendererRegistry => {
	return DynamicForm.Renderers;
};

// Helper function to add a single renderer
export const addDynamicFormRenderer = (fieldType: string, renderer: any) => {
	DynamicForm.Renderers[fieldType] = renderer;
};

// Helper function to remove a renderer
export const removeDynamicFormRenderer = (fieldType: string) => {
	delete DynamicForm.Renderers[fieldType];
};

// Helper function to clear all custom renderers
export const clearDynamicFormRenderers = () => {
	DynamicForm.Renderers = {};
};
